import { ITokenInfo } from "~/types/global"
import {
  IMarketingOsAddToListPerson,
  IMarketingOsContactInList,
  IPersonSearch
} from "~/types/marketing-os"
import { axiosInstance } from "~/utils/axios"

const normalizeTranslated = (value: unknown): unknown =>
  value && typeof value === "object" && !Array.isArray(value)
    ? Object.values(value as Record<string, unknown>)[0]
    : value

const HOT_ACCOUNTS_TRANSLATED_FIELDS = new Set([
  "account_name",
  "short_name",
  "name",
  "location_name",
  "location",
  "primary_industry",
  "industry",
  "context"
])

const normalizeHotAccountsResults = (results: unknown): unknown => {
  if (!Array.isArray(results)) return results
  return results.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item
    const normalizedItem: Record<string, unknown> = { ...item }
    for (const key of HOT_ACCOUNTS_TRANSLATED_FIELDS) {
      if (key in normalizedItem) {
        normalizedItem[key] = normalizeTranslated(normalizedItem[key])
      }
    }
    return normalizedItem
  })
}

export async function getMarketingOsSearchTheme(
  tokenInfo: ITokenInfo,
  payload: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/hot_accounts`

  const response = await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
  if (response?.data?.data?.results) {
    response.data.data.results = normalizeHotAccountsResults(
      response.data.data.results
    )
  }
  return response
}

export async function getMarketingOsQuickPeopleSearch(
  tokenInfo: ITokenInfo,
  payload: IPersonSearch,
  signal?: AbortSignal
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/peopleSearch`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload,
    signal
  })
}

export async function getMarketingOsPeople(
  tokenInfo: ITokenInfo,
  payload: IPersonSearch,
  signal?: AbortSignal
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/peopleSearch`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload,
    signal
  })
}

export async function getMarketingOsCompanies(
  tokenInfo: ITokenInfo,
  search: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/search_companies?search=${search}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function createNewList(tokenInfo: ITokenInfo, listName: string) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      name: listName
    }
  })
}

export async function getMarketingOsList(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function addPersonsToMarketingOsList(
  tokenInfo: ITokenInfo,
  listId: string,
  people: IMarketingOsAddToListPerson[]
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists/${listId}`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      people_details: people
    }
  })
}

export async function getMarketingOsContactsInList(
  tokenInfo: ITokenInfo,
  listId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists/${listId}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function deleteMarketingOsList(
  tokenInfo: ITokenInfo,
  listId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists/${listId}`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function fetchDialerJWTToken(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/dialer/jwt`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function fetchDialerPhoneNumber(
  tokenInfo: ITokenInfo,
  person: IMarketingOsAddToListPerson[],
  isLimitCheck?: boolean,
  isLive?: boolean
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/enrich/phone`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      person_details: person,
      limit_check: isLimitCheck || false,
      is_live: isLive || false
    }
  })
}

export async function fetchMarketingOsPersonEmailAddress(
  tokenInfo: ITokenInfo,
  person: IMarketingOsAddToListPerson[]
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/enrich/email/`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      person_details: person
    }
  })
}

export async function addCallNotes(
  tokenInfo: ITokenInfo,
  callId: string,
  personId: string,
  notes: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/person/${personId}/note`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      content: notes,
      call_id: callId
    }
  })
}

export async function deleteCallNotes(
  tokenInfo: ITokenInfo,
  personId: string,
  noteId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/person/${personId}/note/${noteId}`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function removePersonsFromMarketingOsList(
  tokenInfo: ITokenInfo,
  listId: string,
  personIds: string[]
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists/${listId}/person`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      person_ids: personIds
    }
  })
}

export async function getDialerLogs(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/dialer/logs`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function downloadPeopleList(
  tokenInfo: ITokenInfo,
  listId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists/${listId}/download`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    responseType: "blob"
  })
}

export async function addMarketingOsPhoneNumber(
  tokenInfo: ITokenInfo,
  payload: { val: string; person_details: IMarketingOsContactInList }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/PHONE`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function deleteMarketingOsPhoneNumber(
  tokenInfo: ITokenInfo,
  payload: {
    val: string
    person_details: IMarketingOsContactInList
    id: string
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/PHONE/${payload.id}/delete`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function editMarketingOsPhoneNumber(
  tokenInfo: ITokenInfo,
  payload: {
    val: string
    person_details: IMarketingOsContactInList
    id: string
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/PHONE/${payload.id}`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function addMarketingOsEmail(
  tokenInfo: ITokenInfo,
  payload: { val: string; person_details: IMarketingOsContactInList }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/EMAIL`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function deleteMarketingOsEmail(
  tokenInfo: ITokenInfo,
  payload: {
    val: string
    person_details: IMarketingOsContactInList
    id: string
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/EMAIL/${payload.id}/delete`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function editMarketingOsEmail(
  tokenInfo: ITokenInfo,
  payload: {
    val: string
    person_details: IMarketingOsContactInList
    id: string
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/EMAIL/${payload.id}`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getMarketingOsCampaignContext(
  tokenInfo: ITokenInfo,
  payload: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/hot_accounts/generate_bulk_rationale`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getMarketingOSAccountRationale(
  tokenInfo: ITokenInfo,
  payload: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/hot_accounts/generate_rationale`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function importPeopleList(
  tokenInfo: ITokenInfo,
  listId: string,
  personDetails: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/inside_sales/lists/${listId}/import_people`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: { people_details: personDetails }
  })
}
